// src/components/validationNotes/droitsValidationNotes.js
// ----------------------------------------------------------------------------
// Qui peut voir les saisies de notes des enseignants, côté école.
//
// Deux licences, parce que les deux bouts du circuit sont vendus à part :
// l'espace enseignant (`module.portail_enseignant`), d'où viennent les notes,
// et le carnet (`module.evaluations`), où elles vont. Sans le carnet, il n'y a
// rien à préremplir ; sans l'espace, personne ne saisit.
//
// Des clés fines du module NOTES, résolues par le RÔLE, une famille par
// niveau (décision D3 de Daniel, 7 octobre 2026) :
//   - collège et lycée : NOTES.VALIDATION.VIEW / VALIDER / RECTIFIER ;
//   - primaire         : NOTES.VALIDATION_PRIMAIRE.VIEW / VALIDER / RECTIFIER.
// Le « Directeur du Primaire » et le « Directeur des Etudes » existent côte à
// côte : ouvrir le primaire à l'un ne doit pas lui ouvrir le collège.
//
// AUCUN REPLI, ni d'un niveau sur l'autre, ni sur une porte de module :
// `CARNET_EVALUATION.ACCESS` et `SUIVI_ENSEIGNANTS.ACCESS` sont détenues par
// le groupe Enseignant — un repli montrerait à chaque enseignant les notes de
// ses collègues, et la page de validation des siennes.
//
// Pour chaque niveau, la lecture suit EXACTEMENT celle du serveur
// (NotesEnseignantService.droitsEcole) : VIEW, ou VALIDER, ou RECTIFIER — on
// ne décide pas de ce qu'on ne voit pas, donc décider emporte la lecture.
// Tester VIEW seul laissait sans onglet, et devant « Accès refusé » en venant
// de la cloche, la personne à qui l'on donne VALIDER sans VIEW : or c'est
// justement aux porteurs de VALIDER (et de RECTIFIER) que le serveur envoie
// les notifications « Notes à valider ».
//
// Même composition licence × clé que « Messages des familles » (SidebarNav) et
// l'onglet « Avis des enseignants » (VieScolairePage). Ce test évite un onglet
// ou une page qui ne mèneraient qu'à un refus ; c'est le serveur qui protège,
// et ses `droits` (rendus par chaque lecture) l'emportent sur ce calcul une
// fois la page chargée.
// ----------------------------------------------------------------------------

import { useCallback } from 'react';
import { useFeatures } from '../../contexts/FeaturesContext';
import { usePermissions } from '../../hooks/usePermissions';

/** Collège et lycée. */
export const CLES_VALIDATION = {
  VOIR: 'NOTES.VALIDATION.VIEW',
  VALIDER: 'NOTES.VALIDATION.VALIDER',
  RECTIFIER: 'NOTES.VALIDATION.RECTIFIER',
  // La clé du déverrouillage existant du carnet (verrouillageNotes.routes.js) :
  // autoriser une rectification sur des notes verrouillées passe par lui, aux
  // deux niveaux.
  DEVERROUILLER: 'NOTES.VERROUILLAGE.UNLOCK'
};

/** Primaire : ses propres clés, créées sans attribution, sans repli. */
export const CLES_VALIDATION_PRIMAIRE = {
  VOIR: 'NOTES.VALIDATION_PRIMAIRE.VIEW',
  VALIDER: 'NOTES.VALIDATION_PRIMAIRE.VALIDER',
  RECTIFIER: 'NOTES.VALIDATION_PRIMAIRE.RECTIFIER',
  DEVERROUILLER: CLES_VALIDATION.DEVERROUILLER
};

/** Les clés d'un niveau : `primaire`, ou le collège et le lycée pour tout le reste. */
export const clesDuNiveau = (niveau) => (niveau === 'primaire' ? CLES_VALIDATION_PRIMAIRE : CLES_VALIDATION);

/** Les clés qui ouvrent la lecture du collège et du lycée, comme côté serveur. */
export const CLES_LECTURE = [CLES_VALIDATION.VOIR, CLES_VALIDATION.VALIDER, CLES_VALIDATION.RECTIFIER];

/** Les clés qui ouvrent la lecture du primaire. */
export const CLES_LECTURE_PRIMAIRE = [
  CLES_VALIDATION_PRIMAIRE.VOIR, CLES_VALIDATION_PRIMAIRE.VALIDER, CLES_VALIDATION_PRIMAIRE.RECTIFIER
];

export const LICENCES_VALIDATION = ['module.portail_enseignant', 'module.evaluations'];

const NIVEAU_VIDE = { voir: false, valider: false, rectifier: false };

const oui = (valeur) => valeur === true || valeur === 1 || valeur === '1';

/** `{ voir, valider, rectifier }` d'un niveau, valider ou rectifier emportant la lecture. */
function lireNiveau(brut) {
  if (!brut || typeof brut !== 'object') return { ...NIVEAU_VIDE };
  const valider = oui(brut.valider);
  const rectifier = oui(brut.rectifier);
  const voir = oui(brut.voir) || valider || rectifier;
  return { voir, valider: voir && valider, rectifier: voir && rectifier };
}

/**
 * Les droits rendus par le serveur, rangés par niveau :
 * `{ secondaire, primaire, deverrouiller, forcer }`.
 *
 * Forme N3 : `{ secondaire: {…}, primaire: {…}, deverrouiller, forcer }`.
 * Forme N1, tolérée (serveur pas encore à jour) : `{ voir, valider, rectifier,
 * deverrouiller }`, qui ne parle que du collège et du lycée — le primaire
 * reste alors fermé, jamais déduit des clés du secondaire.
 */
export function droitsParNiveau(droits) {
  const d = droits && typeof droits === 'object' ? droits : {};
  const formeN3 = 'secondaire' in d || 'primaire' in d;
  return {
    secondaire: lireNiveau(formeN3 ? d.secondaire : d),
    primaire: lireNiveau(formeN3 ? d.primaire : null),
    deverrouiller: oui(d.deverrouiller),
    forcer: oui(d.forcer)
  };
}

/**
 * Les droits d'une page qui ne parle que d'UN niveau (une feuille, le mois
 * d'une classe) : `{ voir, valider, rectifier, deverrouiller, forcer, est_auteur }`.
 * Le serveur peut les rendre à plat (déjà résolus pour ce niveau) ou rangés
 * par niveau : on lit le bon tiroir, jamais celui de l'autre niveau.
 */
export function droitsDuNiveau(droits, niveau) {
  const d = droits && typeof droits === 'object' ? droits : {};
  const tiroir = niveau === 'primaire' ? d.primaire : d.secondaire;
  const base = tiroir && typeof tiroir === 'object' ? lireNiveau(tiroir) : lireNiveau(d);
  return {
    ...base,
    deverrouiller: oui(d.deverrouiller),
    forcer: oui(d.forcer),
    est_auteur: oui(d.est_auteur)
  };
}

export function useAccesValidationNotes() {
  const { estActive, chargement: chargementLicence } = useFeatures();
  const { detailedPermissions = [], loading } = usePermissions();

  // Pas de raccourci ALL_MODULES : le serveur développe le passe-partout dans
  // la liste détaillée et en retire les refus individuels (voir SidebarNav).
  const aLaCle = useCallback((cle) => detailedPermissions.some(
    (permission) => (permission.permission_key || permission.key) === cle
  ), [detailedPermissions]);

  const lectureSecondaire = CLES_LECTURE.some((cle) => aLaCle(cle));
  const lecturePrimaire = CLES_LECTURE_PRIMAIRE.some((cle) => aLaCle(cle));
  const lecture = lectureSecondaire || lecturePrimaire;

  // Tant que la licence se charge, `estActive` répond « oui » à tout (même
  // politique que le serveur quand rien n'est connu) : on attend la réponse,
  // sinon l'onglet apparaîtrait un instant — et sa première lecture partirait —
  // dans une école qui n'a pas l'espace enseignant.
  const licences = !chargementLicence && LICENCES_VALIDATION.every((cle) => estActive(cle));

  return {
    chargement: Boolean(loading || chargementLicence),
    licences,
    // Au moins un des deux niveaux : l'onglet du carnet, l'onglet du suivi.
    lecture,
    lectureSecondaire,
    lecturePrimaire,
    voir: licences && lecture,
    voirSecondaire: licences && lectureSecondaire,
    voirPrimaire: licences && lecturePrimaire,
    aLaCle
  };
}
